import { event as sendGaEvent } from '@/utils/ga'

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
    mobiBridge?: {
      postMessage: (message: string) => void
    }
    ReactNativeWebview?: {
      postMessage: (message: string) => void
    }
  }
}

type TrackingValue = string | number | boolean | null | undefined

const FLOW_NAME = 'new_number'

const normalizeTrackingText = (value?: string | null): string => {
  if (value == null || value.trim() === '') {
    return 'unknown'
  }

  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

const sanitizeParams = (
  params: Record<string, TrackingValue>
): Record<string, string | number | boolean> => {
  return Object.entries(params).reduce<Record<string, string | number | boolean>>(
    (acc, [key, value]) => {
      if (value === undefined || value === null || value === '') {
        return acc
      }

      if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      ) {
        acc[key] = value
      }

      return acc
    },
    {}
  )
}

const getPlatform = (): 'app' | 'web' => {
  if (typeof window === 'undefined') {
    return 'web'
  }

  return window.mobiBridge != null || window.ReactNativeWebview != null
    ? 'app'
    : 'web'
}

const getDeviceType = (): 'ios' | 'android' | 'desktop' => {
  if (typeof navigator === 'undefined') {
    return 'desktop'
  }

  const userAgent = navigator.userAgent.toLowerCase()

  if (['iphone', 'ipad', 'ipod', 'ios'].some((device) => userAgent.includes(device))) {
    return 'ios'
  }

  if (userAgent.includes('android')) {
    return 'android'
  }

  return 'desktop'
}

const getSource = (): string => {
  if (typeof window === 'undefined') {
    return 'organic'
  }

  const params = new URLSearchParams(window.location.search)
  const explicitSource = params.get('utm_source') ?? params.get('source')

  if (explicitSource != null && explicitSource.trim() !== '') {
    return normalizeTrackingText(explicitSource)
  }

  if (
    params.has('utm_campaign') ||
    params.has('utm_medium') ||
    params.has('gclid') ||
    params.has('fbclid')
  ) {
    return 'campaign'
  }

  return 'organic'
}

const trackNewNumberEvent = (
  eventName: string,
  params: Record<string, TrackingValue> = {}
): void => {
  if (typeof window === 'undefined') {
    return
  }

  const normalizedEventName = normalizeTrackingText(eventName)
  const payload = {
    event: normalizedEventName,
    flow_name: FLOW_NAME,
    platform: getPlatform(),
    device_type: getDeviceType(),
    source: getSource(),
    debug_mode: true,
    ...sanitizeParams(params)
  }

  window.dataLayer = window.dataLayer ?? []
  window.dataLayer.push(payload)

  const { event: _eventName, ...gaParams } = payload
  sendGaEvent(normalizedEventName, gaParams)

  if (process.env.NODE_ENV !== 'production') {
    console.log('[GA4][new_number]', payload)
  }
}

const getPrefixFromNumber = (number?: string): string => {
  if (number == null) {
    return 'unknown'
  }

  const digits = number.replace(/\D/g, '')

  return digits.length >= 2 ? digits.slice(0, 2) : 'unknown'
}

const getPatternType = (number?: string, fallback?: string | null): string => {
  if (fallback != null && fallback.trim() !== '') {
    return normalizeTrackingText(fallback)
  }

  const digits = (number ?? '').replace(/\D/g, '')

  if (digits.length === 0) {
    return 'unknown'
  }

  if (/^(\d)\1+$/.test(digits)) {
    return 'repeated'
  }

  const reversed = digits.split('').reverse().join('')

  if (digits === reversed) {
    return 'palindrome'
  }

  if (
    ['0123', '1234', '2345', '3456', '4567', '5678', '6789']
      .some((pattern) => digits.includes(pattern))
  ) {
    return 'ascending'
  }

  if (
    ['9876', '8765', '7654', '6543', '5432', '4321', '3210']
      .some((pattern) => digits.includes(pattern))
  ) {
    return 'descending'
  }

  return 'standard'
}

export {
  getPatternType,
  getPrefixFromNumber,
  normalizeTrackingText,
  trackNewNumberEvent
}
